import { Component, ReactNode } from "react";
import { errorLogger } from "@/lib/errorLogger";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

const MESSAGES: Record<string, { title: string; body: string; button: string }> = {
  ru: {
    title: "Упс! Что-то пошло не так",
    body: "Не волнуйся — твой питомец в безопасности. Попробуй обновить страницу.",
    button: "Обновить страницу",
  },
  es: {
    title: "¡Vaya! Algo salió mal",
    body: "No te preocupes — tu mascota está a salvo. Intenta recargar la página.",
    button: "Recargar página",
  },
  en: {
    title: "Oops! Something went wrong",
    body: "Don't worry — your pet is safe. Try refreshing the page.",
    button: "Refresh Page",
  },
};

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    errorLogger.error("ErrorBoundary caught", error, {
      componentStack: errorInfo.componentStack?.slice(0, 500),
    });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      const lang = (document.documentElement.lang || "en").slice(0, 2);
      const msg = MESSAGES[lang] || MESSAGES.en;
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-background p-6 text-center">
          <span className="text-5xl mb-4">🐾</span>
          <h1 className="text-xl font-bold text-foreground mb-2">{msg.title}</h1>
          <p className="text-muted-foreground mb-6 max-w-sm">{msg.body}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-xl hover:opacity-90 transition font-semibold"
          >
            {msg.button}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
